import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Tiny stale-while-revalidate cache for API data.
 *
 * Why: every route used to fetch from zero on mount (blank → empty shell →
 * data pop-in), which made the app feel like a webpage. This module keeps
 * the last known response per key in memory AND in localStorage, so pages
 * paint instantly with the previous data and refresh silently in the
 * background — across navigations and across full app restarts.
 *
 * Rules:
 * - Data is household-scoped. The cache is bound to `userId:householdId`
 *   (see bindCacheOwner); a mismatch wipes everything before anything renders.
 * - Errors never clobber stale data: a failed revalidate keeps the last
 *   known value on screen.
 * - `refreshAll()` re-runs every fetcher that is currently mounted — the
 *   soft pull-to-refresh path.
 */

const STORAGE_KEY = "logbuch:swr:v1";
// Entries older than this are dropped on boot — stale enough to be confusing.
const MAX_AGE_MS = 7 * 24 * 3600 * 1000;
// Skip persisting single entries larger than this (memory-only instead).
const MAX_ENTRY_CHARS = 250_000;

interface StoredBlob {
  owner: string | null;
  entries: Record<string, { v: unknown; t: number }>;
}

const mem = new Map<string, unknown>();
let owner: string | null = null;

interface Subscription {
  fetcher: () => Promise<unknown>;
  listeners: Set<(v: unknown) => void>;
}
const subs = new Map<string, Subscription>();
const inflight = new Map<string, Promise<unknown>>();

// ---- persistence -----------------------------------------------------------

function boot() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const blob = JSON.parse(raw) as StoredBlob;
    owner = blob.owner ?? null;
    const now = Date.now();
    for (const [k, e] of Object.entries(blob.entries ?? {})) {
      if (e && typeof e.t === "number" && now - e.t < MAX_AGE_MS) {
        mem.set(k, e.v);
      }
    }
  } catch {
    // Corrupt blob — start clean.
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }
}
boot();

let persistTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePersist() {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(persistNow, 400);
}

function persistNow() {
  persistTimer = null;
  const entries: Record<string, { v: unknown; t: number }> = {};
  const now = Date.now();
  for (const [k, v] of mem) {
    try {
      if (JSON.stringify(v).length <= MAX_ENTRY_CHARS) entries[k] = { v, t: now };
    } catch {
      // Unserialisable value — memory-only.
    }
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ owner, entries }));
  } catch {
    // Quota — drop persistence rather than break the app.
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }
}

// ---- core ------------------------------------------------------------------

/** Wipe everything (logout, household switch). */
export function clearCache() {
  mem.clear();
  owner = null;
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

/** Bind the cache to `userId:householdId`. A different owner than the one
 *  the cache was written under wipes it — another user / another household
 *  on the same device must never see the previous one's numbers. */
export function bindCacheOwner(next: string) {
  if (owner !== null && owner !== next) {
    mem.clear();
    subs.forEach((s) => s.listeners.forEach((l) => l(undefined)));
  }
  owner = next;
  schedulePersist();
}

export function cacheGet<T>(key: string): T | undefined {
  return mem.get(key) as T | undefined;
}

/** Write a value directly (e.g. optimistic update after a mutation) and
 *  notify every mounted subscriber of the key. */
export function cacheSet<T>(key: string, value: T) {
  mem.set(key, value);
  subs.get(key)?.listeners.forEach((l) => l(value));
  schedulePersist();
}

/** Re-run the (latest mounted) fetcher for a key. Deduped: concurrent calls
 *  share one request. Errors keep the stale value and reject. */
export function revalidate(key: string): Promise<unknown> {
  const sub = subs.get(key);
  if (!sub) return Promise.resolve(mem.get(key));
  const running = inflight.get(key);
  if (running) return running;
  const p = sub
    .fetcher()
    .then((v) => {
      inflight.delete(key);
      cacheSet(key, v);
      return v;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });
  inflight.set(key, p);
  return p;
}

/** Soft refresh: revalidate every mounted key. Returns how many keys were
 *  mounted — 0 means the current page doesn't use the cache (caller may
 *  fall back to a hard reload). */
export async function refreshAll(): Promise<number> {
  const keys = [...subs.keys()];
  await Promise.allSettled(keys.map((k) => revalidate(k)));
  // Pages with bespoke loaders can hook the same gesture.
  window.dispatchEvent(new CustomEvent("logbuch:refresh"));
  return keys.length;
}

// ---- hook ------------------------------------------------------------------

export interface SWRResult<T> {
  /** Last known value — instantly from cache when available, else undefined
   *  until the first fetch resolves. */
  data: T | undefined;
  /** True only when there is no data at all yet (first ever load). */
  loading: boolean;
  /** Force a refetch (after a mutation). Errors are swallowed — stale data
   *  stays on screen; pass through the rejection via the returned promise. */
  refresh: () => Promise<void>;
  /** Error of the LAST revalidation attempt; null once one succeeds. */
  error: unknown;
}

/**
 * Cache-first data hook. `key` must uniquely identify the request including
 * all parameters (e.g. `budget:2026-08`). Pass `null` to disable.
 */
export function useSWR<T>(key: string | null, fetcher: () => Promise<T>): SWRResult<T> {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [state, setState] = useState<{ key: string | null; data: T | undefined; error: unknown }>(
    () => ({ key, data: key ? (mem.get(key) as T | undefined) : undefined, error: null }),
  );
  // Key changed mid-render (month switch etc.): swap to the new key's cached
  // value immediately so the old key's data never paints under the new one.
  if (state.key !== key) {
    setState({ key, data: key ? (mem.get(key) as T | undefined) : undefined, error: null });
  }

  useEffect(() => {
    if (!key) return;
    let sub = subs.get(key);
    if (!sub) {
      sub = { fetcher: () => fetcherRef.current(), listeners: new Set() };
      subs.set(key, sub);
    }
    // Latest mounted consumer owns the fetcher (params may have changed).
    sub.fetcher = () => fetcherRef.current();
    const listener = (v: unknown) =>
      setState({ key, data: v as T | undefined, error: null });
    sub.listeners.add(listener);
    revalidate(key).catch((err) => {
      setState((s) => (s.key === key ? { ...s, error: err } : s));
    });
    return () => {
      const s = subs.get(key);
      if (!s) return;
      s.listeners.delete(listener);
      if (s.listeners.size === 0) subs.delete(key);
    };
  }, [key]);

  const refresh = useCallback(async () => {
    if (!key) return;
    try {
      await revalidate(key);
    } catch {
      // stale data stays on screen
    }
  }, [key]);

  return { data: state.data, loading: state.data === undefined, refresh, error: state.error };
}
